/**
 * Formats a given date input into the "MM-DD-YYYY" string format.
 *
 * Accepts either a Date object or a string in ISO format (e.g., "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss").
 * Returns an empty string for invalid or unrecognized inputs.
 *
 * @param {Date|string} dateInput - The date to format, as a Date object or ISO date string.
 * @returns {string} The formatted date string in "MM-DD-YYYY" format, or an empty string if input is invalid.
 */
export const formatDateToMmDdYyyy = (dateInput) => {
    if (!dateInput) {
        return '';
    }

    let year, month, day;
    
    // Handle different input formats
    if (typeof dateInput === 'string') {
        // Remove any extra whitespace
        const cleanedString = dateInput.trim();
        
        // Handle ISO format (YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss, etc.)
        if (/^\d{4}-\d{1,2}-\d{1,2}/.test(cleanedString)) {
            const parts = cleanedString.split('T')[0].split('-'); // Remove time part if present
            year = parseInt(parts[0]);
            month = parseInt(parts[1]);
            day = parseInt(parts[2]);
        }
    } else if (dateInput instanceof Date) {
        // Handle Date objects
        year = dateInput.getFullYear();
        month = dateInput.getMonth() + 1;
        day = dateInput.getDate();
    } else {
        console.warn(`Invalid date format: ${dateInput}`);
        return '';
    }

    // Validate the parsed date components
    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
        console.warn(`Invalid date components: ${dateInput}`);
        return '';
    }

    // Format as MM-DD-YYYY
    const formattedMonth = String(month).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    return `${formattedMonth}-${formattedDay}-${year}`;
};

/**
 * Formats a given date input into the 'YYYY-MM-DD' string format.
 *
 * Accepts a Date object or a string in 'YYYY-MM-DD' or 'MM-DD-YYYY' format.
 * Returns an empty string for invalid or unrecognized inputs.
 *
 * @param {Date|string} dateInput - The date to format. Can be a Date object or a string in 'YYYY-MM-DD', 'MM-DD-YYYY', or 'MM/DD/YYYY' format.
 * @returns {string} The formatted date string in 'YYYY-MM-DD' format, or an empty string if input is invalid.
 */
export const formatDateToYyyyMmDd = (dateInput) => {
    if (!dateInput) {
        return '';
    }

    let year, month, day;
    
    if (typeof dateInput === 'string') {
        // Remove any extra whitespace
        const cleanedString = dateInput.trim();

        // Handle format YYYY-MM-DD
        if (/^\d{4}-\d{1,2}-\d{1,2}/.test(cleanedString)) {
            const parts = cleanedString.split('-');
            month = parseInt(parts[1]);
            day = parseInt(parts[2]);
            year = parseInt(parts[0]);
        }

        // Handle format MM-DD-YYYY or MM/DD/YYYY
        if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}/.test(cleanedString)) {
            const parts = cleanedString.split(/[-/]/);
            month = parseInt(parts[0]);
            day = parseInt(parts[1]);
            year = parseInt(parts[2]);
        }
    } else if (dateInput instanceof Date) {
        // Handle Date objects
        year = dateInput.getFullYear();
        month = dateInput.getMonth() + 1;
        day = dateInput.getDate();
    } else {
        console.warn(`Invalid date format: ${dateInput}`);
        return '';
    }

    // Validate the parsed date components
    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
        console.warn(`Invalid date components: ${dateInput}`);
        return '';
    }

    // Format as YYYY-MM-DD
    const formattedMonth = String(month).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    return `${year}-${formattedMonth}-${formattedDay}`;
};

/**
 * Formats a date input into "MM/DD/YYYY" string format for display.
 *
 * Accepts a Date object or an ISO date string (e.g., "YYYY-MM-DD").
 * Returns '-' for null/undefined and '' for invalid inputs.
 *
 * @param {Date|string} dateInput
 * @returns {string}
 */
export const formatDateForDisplay = (dateInput) => {
    if (!dateInput) return '-';

    let year, month, day;

    if (typeof dateInput === 'string') {
        const parts = dateInput.trim().split('T')[0].split('-');
        if (parts.length !== 3) return '';
        [year, month, day] = parts.map(Number);
    } else if (dateInput instanceof Date) {
        year = dateInput.getFullYear();
        month = dateInput.getMonth() + 1;
        day = dateInput.getDate();
    } else {
        return '';
    }

    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) return '';

    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
};
